import RoutineRecordApi, {
  ModifiedRoutineRecord,
} from '~/common/api/zeiterfassungServerApi/RoutineRecordApi';
import {
  Button,
  DefaultAlertDialog,
  Text,
  useToast,
} from '~/common/components/defaultUIComponents';
import {
  Flex,
  Grid,
  IconButton,
  Select,
  useDisclosure,
} from '@chakra-ui/react';
import { RoutineApi } from '~/common/api/zeiterfassungServerApi/RoutineApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { RoutineRecordListItem } from './RoutineRecordListItem';
import { formatName } from '~/common/utils/employeeUtils';
import { ListView } from '~/common/components/ListView';
import useTranslate from '~/common/utils/translate';
import { createRef, useState } from 'react';
import _ from 'lodash';

type FilterState = {
  employee?: number;
  routine?: number;
};

export const RoutineRecordsWithPictures = () => {
  const t = useTranslate();
  const [page, setPage] = useState(0);
  const [filterState, setFilterState] = useState<FilterState>({});
  const employeeRef = createRef<HTMLSelectElement>();
  const routineRef = createRef<HTMLSelectElement>();

  const handleClick = () => {
    setFilterState((filterState) => {
      const newFilter = { ...filterState };
      if (employeeRef.current && routineRef.current) {
        if (employeeRef.current.value && employeeRef.current.value !== '-1') {
          newFilter.employee = parseInt(employeeRef.current.value);
        } else {
          newFilter.employee = undefined;
        }
        if (routineRef.current.value && routineRef.current.value !== '-1') {
          newFilter.routine = parseInt(routineRef.current.value);
        } else {
          newFilter.routine = undefined;
        }
      }
      return newFilter;
    });
  };

  const resetFilters = () => {
    setFilterState({});
    if (employeeRef.current && routineRef.current) {
      employeeRef.current.value = '-1';
      routineRef.current.value = '-1';
    }
  };

  const { isOpen, onToggle } = useDisclosure();

  const { data: recordCount } =
    RoutineRecordApi.hooks.useRoutinesWithPicturesCount();
  const { data: records, isLoading } =
    RoutineRecordApi.hooks.useRoutinesWithPictures(page, filterState);

  const { mutateAsync: bulkDelete, isLoading: isDeleting } =
    RoutineRecordApi.hooks.useBulkDeleteRoutinesWithPictures(page);

  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const [{ data: routines }] = RoutineApi.hooks.useRoutinesQuery();

  const toast = useToast({});

  const handleDeleteClick = async () => {
    const { employee, routine } = filterState;
    if (employee || routine) {
      await bulkDelete({ employee, routine }).then(onToggle);
      toast({ title: 'deletion.success', status: 'success' });
    }
  };
  return (
    <>
      <DefaultAlertDialog
        dialogHeaderText={
          'dashboard.routineRecord.bulkDelete.confirmation.header'
        }
        dialogBodyText={'dashboard.routineRecord.bulkDelete.confirmation'}
        isOpen={isOpen}
        onConfirmedAction={() =>
          void handleDeleteClick().catch(() =>
            toast({ title: 'generic.error', status: 'error' }),
          )
        }
        onCancel={onToggle}
        isLoading={isDeleting}
      />

      <Flex direction="row" justify="space-between" mb={3}>
        <Flex gap={3}>
          <Select
            ref={employeeRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectEmployee')}</option>
            {_.orderBy(employees, [(e) => formatName(e)], ['asc'])?.map((e) => (
              <option key={e.id} value={e.id}>
                {formatName(e)}
              </option>
            ))}
          </Select>
          <Select
            ref={routineRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectRoutine')}</option>
            {_.orderBy(routines, ['name'], ['asc'])?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
          <Flex gap={3} alignItems="center">
            {(filterState?.routine || filterState?.employee) && (
              <Text
                textDecoration="underline"
                cursor="pointer"
                color="green.600"
                style={{ whiteSpace: 'nowrap' }}
                onClick={resetFilters}
              >
                resetFilter
              </Text>
            )}
            <Button onClick={handleClick}>search</Button>
            <Button
              disabled={!filterState.routine && !filterState.employee}
              onClick={onToggle}
            >
              delete
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <Flex direction="column" alignItems="center">
        <Flex gap={3} alignItems="center" mb={3}>
          <IconButton
            aria-label="next"
            icon={<ArrowBackIcon />}
            onClick={() => setPage(Math.max(page - 1, 0))}
          />
          <Text size="lg">{page + 1}</Text>
          <IconButton
            aria-label="next"
            icon={<ArrowForwardIcon />}
            onClick={() =>
              setPage(Math.min(page + 1, Math.floor((recordCount ?? 0) / 100)))
            }
          />
        </Flex>
        <ListView
          data={records as ModifiedRoutineRecord[]}
          search=""
          searchFields={() => []}
          isLoading={isLoading}
          render={({ data }) => (
            <Grid templateColumns="repeat(2, 1fr)" gap={3}>
              {data.map((record) => (
                <RoutineRecordListItem key={record.id} record={record} />
              ))}
            </Grid>
          )}
        />
        <Flex gap={3} alignItems="center" mt={3}>
          <IconButton
            aria-label="next"
            icon={<ArrowBackIcon />}
            onClick={() => setPage(Math.max(page - 1, 0))}
          />
          <Text size="lg">{page + 1}</Text>
          <IconButton
            aria-label="next"
            icon={<ArrowForwardIcon />}
            onClick={() =>
              setPage(Math.min(page + 1, Math.floor((recordCount ?? 0) / 100)))
            }
          />
        </Flex>
      </Flex>
    </>
  );
};
